import { useBrandPlanSchedules, Props, Options } from 'hooks/queries/schedules/useBrandPlanSchedules'
import { Response } from 'request-dot-js'
import {
  TransformedBrandPlanSchedule,
  TransformedBrandPlanScheduleListItem,
} from 'screens/BrandKits/components/BrandPlanTimeline/Types'
import { BrandPlanSchedule, BrandPlanScheduleListItem } from 'types'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

dayjs.extend(isBetween)
dayjs.extend(isSameOrBefore)

// Function overloads so detail callers get `TransformedBrandPlanSchedule` and
// list-view callers (`useSchedules` / `useBrandPlanSchedules` / this hook) get
// `TransformedBrandPlanScheduleListItem` — same runtime behaviour, honest types.
export function transformSchedule<S extends BrandPlanSchedule>(schedule: S): TransformedBrandPlanSchedule<S>
export function transformSchedule<S extends BrandPlanScheduleListItem>(
  schedule: S,
): TransformedBrandPlanScheduleListItem<S>
export function transformSchedule(schedule: BrandPlanScheduleListItem) {
  const startDate = dayjs(schedule.start_date)
  const endDate = startDate.add(schedule.length, 'week').subtract(1, 'day').endOf('day')

  return {
    ...schedule,
    startDate,
    endDate,
    approvalDate: schedule.approval_date ? dayjs(schedule.approval_date) : undefined,
    createdAt: dayjs(schedule.created_at),
  }
}

export const useTransformedBrandPlanSchedules = (
  props: Props,
  options?: Omit<Options<Response<{ schedules: TransformedBrandPlanScheduleListItem[] }>>, 'select'>,
) =>
  useBrandPlanSchedules<Response<{ schedules: TransformedBrandPlanScheduleListItem[] }>>(props, {
    select: (data) => {
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          data: {
            ...page.data,
            schedules: page.data.schedules.map((s: BrandPlanScheduleListItem) => transformSchedule(s)),
          },
        })),
      }
    },
    ...options,
  })
